/* Bottle + free treat dispenser quick-add.
 *
 * "Add a dog water bottle and get a free treat dispenser." Adding it puts BOTH items
 * in the cart under one bundleId — the bottle at its own price and the dispenser at
 * $0 (the unlisted free-treat-dispenser mirror), so the pair reads as a bundle priced
 * at the bottle. Same grouping properties the set builder uses, so the cart's bundle
 * rendering treats it identically.
 */
(function () {
  "use strict";

  const DEFAULTS = {
    bottle: "20oz-flip-dog-travel-water-bottle",
    free: "free-treat-dispenser",
    text: "Add a dog water bottle",
    callout: "Free Treat Dispenser"
  };

  const money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    return "$" + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  };
  const uuid = () => (crypto?.randomUUID?.() || "off-" + Date.now() + Math.random().toString(16).slice(2));
  const toGid = (id) => "gid://shopify/ProductVariant/" + id;

  /* Mirror what the main Add to Cart does: re-render the drawer sections, then open
   * it through the theme's own events, falling back to clicking its toggle. */
  async function refreshAndOpenCart() {
    /* <cart-drawer> is a custom element that owns its markup and refetches itself.
     * Overwriting its innerHTML destroyed it — which is why the drawer opened empty.
     * Ask it to refresh instead, then fire the theme's own open events. */
    const drawer = document.querySelector("cart-drawer");
    if (drawer && typeof drawer.refetchContents === "function") {
      try { await drawer.refetchContents(); } catch (e) {}
    }
    ["fluco:modal:cartDrawer:opening", "fluco:modal:cartDrawer:open"].forEach((n) => {
      try { window.dispatchEvent(new CustomEvent(n)); } catch (e) {}
    });
  }

  const cache = {};
  async function product(handle) {
    if (cache[handle]) return cache[handle];
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`${handle}: ${res.status}`);
    cache[handle] = await res.json();
    return cache[handle];
  }

  function firstAvailable(p) {
    return (p.variants || []).find((v) => v.available) || (p.variants || [])[0] || null;
  }
  function byColour(p, wanted) {
    const want = String(wanted || "").trim().toLowerCase();
    if (!want) return firstAvailable(p);
    const hit = (p.variants || []).find(
      (v) => String(v.title).trim().toLowerCase() === want && v.available !== false
    );
    return hit || firstAvailable(p);
  }

  /* The colour the set is currently configured to, so the add-on matches it. */
  function setColour() {
    const sw = document.querySelector(
      ".mpb-shared-color .mpb-swatch.is-active, .mpb-opt--color .mpb-swatch.is-active"
    );
    return sw ? (sw.getAttribute("aria-label") || "").trim() : "";
  }

  async function build(root, mount) {
    const cfg = {
      bottle: root.dataset.offerProduct || DEFAULTS.bottle,
      free: root.dataset.offerFreeProduct || DEFAULTS.free,
      text: root.dataset.offerText || DEFAULTS.text,
      callout: root.dataset.offerCallout || DEFAULTS.callout
    };

    let bottle, free = null;
    try {
      bottle = await product(cfg.bottle);
      if (cfg.free) free = await product(cfg.free);   // blank = plain add-on, no giveaway
    } catch (e) {
      return;                       // offer simply doesn't render if the product is missing
    }
    const bv = firstAvailable(bottle);
    if (!bv) return;
    const priceOf = bv.price;

    const card = document.createElement("div");
    card.className = "wsl-offer";
    card.innerHTML = `
      <div class="wsl-offer__media">
        ${bottle.featured_image ? `<img src="${bottle.featured_image}&width=200" alt="">` : ``}
        ${free ? `<span class="wsl-offer__plus">+</span>` : ``}
        ${free && free.featured_image ? `<img src="${free.featured_image}&width=200" alt="">` : ``}
      </div>
      <div class="wsl-offer__body">
        <p class="wsl-offer__text">${cfg.text}</p>
        ${cfg.callout ? `<span class="wsl-offer__tag">${cfg.callout}</span>` : ``}
      </div>
      <div class="wsl-offer__action">
        <span class="wsl-offer__now">${money(bv.price)}</span>
        <button type="button" class="wsl-offer__btn">Add</button>
      </div>`;

    /* Keep the thumbnails on whatever colour the set is showing. */
    const imgs = card.querySelectorAll(".wsl-offer__media img");
    function syncImages() {
      const colour = setColour();
      const pick = [byColour(bottle, colour), free ? byColour(free, colour) : null];
      pick.forEach((v, i) => {
        const el = imgs[i];
        if (!el || !v) return;
        const src = (v.featured_image && v.featured_image.src) ||
                    (i === 0 ? bottle.featured_image : free.featured_image);
        if (src) el.src = src + (src.includes("?") ? "&" : "?") + "width=200";
      });
      const bvNow = byColour(bottle, colour);
      const nowEl = card.querySelector(".wsl-offer__now");
      if (nowEl && bvNow) nowEl.textContent = money(bvNow.price);
    }
    syncImages();
    document.addEventListener("click", (e) => {
      if (e.target.closest && e.target.closest(".mpb-swatch")) setTimeout(syncImages, 40);
    });

    const btn = card.querySelector(".wsl-offer__btn");
    btn.addEventListener("click", async () => {
      if (btn.disabled) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = "Adding…";
      try {
        /* Resolve at click time so the pair follows whatever colour the set is on. */
        const colour = setColour();
        const bvPick = byColour(bottle, colour) || bv;
        const fv = free ? byColour(free, bvPick.title) : null;
        const bundleId = uuid();
        const props = {
          bundleId,
          bundleParentVariantId: toGid(bvPick.id),
          bundleTitle: "Bottle + Free Treat Dispenser"
        };
        const items = [
          { id: String(bvPick.id), quantity: 1, properties: { ...props } }
        ];
        if (fv) {
          items.push({
            id: String(fv.id),
            quantity: 1,
            properties: { ...props, _FreeItem: "1" }
          });
        }
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(await res.text());

        /* Confirm the free item actually landed. A multi-item add can come back OK
         * while the $0 line is dropped or merged away, which reads as "only the bottle
         * was added" — so check and add it on its own if it's missing. */
        if (fv) {
          try {
            const cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
            /* The bundling app merges the pair into one line keyed on the parent, so
             * a missing free line here is normal — only re-add if neither the free
             * variant nor a merged bundle line is present. */
            const present = (cart.items || []).some(
              (it) => String(it.variant_id) === String(fv.id) ||
                      (it.properties && it.properties.bundleId === bundleId) ||
                      (it.properties && it.properties._Details)
            );
            if (!present) {
              await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ items: [items[1]] })
              });
            }
          } catch (e) { /* the pair is already in; don't block the drawer on a check */ }
        }

        btn.textContent = "Added";
        await refreshAndOpenCart();
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
      } catch (e) {
        btn.textContent = "Try again";
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
      }
    });

    mount.parentNode.insertBefore(card, mount.nextSibling);
  }

  function boot() {
    if (!document.querySelector('[data-name-mode="1"]')) return;
    const root = document.querySelector(".mpb");
    if (!root || root.dataset.wslOfferReady === "1") return;
    /* Sit just below the reassurance row. */
    const mount = document.querySelector(".wsl__trust");
    if (!mount) return;
    root.dataset.wslOfferReady = "1";
    build(root, mount);
  }

  ["DOMContentLoaded", "shopify:section:load"].forEach((e) => document.addEventListener(e, boot));
  if (document.readyState !== "loading") boot();
  /* The trust row is built by walk-set-layout.js, which may land after us. */
  let tries = 0;
  const t = setInterval(() => { boot(); if (++tries > 40) clearInterval(t); }, 200);
})();
